import { useCallback, useMemo } from "react";

import { useSelector } from "react-redux";

import { AccountWithCode } from "@/jenesys-types/data/common";
import { DataType } from "@/jenesys-types/data/enums";
import {
  ListSearchComparator,
  ListSearchCriteria,
  ListSearchCriterion
} from "@/jenesys-types/list";

import { useCrudListContext } from "@/components/common/crud/CrudList/CrudListContext";
import { selectAccountingShortData } from "@/store/features/clientSettings/connections/connections";

export interface FilterFields {
  supplier?: string;
  total?: string;
  dateFrom?: string;
  dateTo?: string;
  accountCode?: string;
  vatAmount?: string;
  uploadedDateFrom?: string;
  uploadedDateTo?: string;
  source?: string;
  status?: string[];
  uploadedBy?: string;
  invoiceId?: string;
}

export interface UseCrudListFilterReturn {
  applyFilters: (filters: FilterFields) => void;
  clearFilters: () => void;
  currentFilters: FilterFields;
}

const formatNumericValue = (value: string): string => {
  if (!value) return "";
  const num = parseFloat(value);
  if (Number.isNaN(num)) return "";
  return Number.isInteger(num) ? `${num.toString()}.` : num.toString();
};

/**
 * Creates search criteria for filtering based on the provided filter fields
 */
const createFilterCriteria = (
  filters: FilterFields,
  accountCodes: AccountWithCode[],
  dataType?: DataType
): ListSearchCriteria | undefined => {
  const criteria: ListSearchCriteria = {};
  let hasFilters = false;

  if (filters.supplier) {
    // For BankStatement, map 'supplier' to 'supplier_name' in the API
    if (dataType === DataType.BankStatement) {
      criteria.supplier = {
        comparator: ListSearchComparator.Contains,
        value: filters.supplier
      };
    } else {
      criteria.supplier_name = {
        comparator: ListSearchComparator.Contains,
        value: filters.supplier
      };
    }
    hasFilters = true;
  }

  if (filters.total) {
    // For BankStatement, map 'total' to 'amount' in the API with equals operator
    if (dataType === DataType.BankStatement) {
      criteria.amount = {
        comparator: ListSearchComparator.Equal,
        value: formatNumericValue(filters.total)
      };
    } else {
      criteria.total = {
        comparator: ListSearchComparator.Contains,
        value: formatNumericValue(filters.total)
      };
    }
    hasFilters = true;
  }

  if (filters.dateFrom) {
    // For BankStatement, map 'dateFrom' to 'date' in the API
    if (dataType === DataType.BankStatement) {
      criteria.date = {
        comparator: ListSearchComparator.GreaterThanOrEqual,
        value: filters.dateFrom
      };
    } else {
      criteria.date_of_invoice = {
        comparator: ListSearchComparator.GreaterThanOrEqual,
        value: filters.dateFrom
      };
    }
    hasFilters = true;
  }

  if (filters.dateTo) {
    // For BankStatement, map 'dateTo' to 'date' in the API
    if (dataType === DataType.BankStatement) {
      // If date was also specified, we need to make date an array
      if (criteria.date) {
        const dateFrom = criteria.date as ListSearchCriterion;
        criteria.date = [
          dateFrom,
          {
            comparator: ListSearchComparator.LessThanOrEqual,
            value: filters.dateTo
          }
        ];
      } else {
        criteria.date = {
          comparator: ListSearchComparator.LessThanOrEqual,
          value: filters.dateTo
        };
      }
    } else {
      // For other data types, use date_of_invoice
      // eslint-disable-next-line no-lonely-if
      if (criteria.date_of_invoice) {
        const dateFrom = criteria.date_of_invoice as ListSearchCriterion;
        criteria.date_of_invoice = [
          dateFrom,
          {
            comparator: ListSearchComparator.LessThanOrEqual,
            value: filters.dateTo
          }
        ];
      } else {
        criteria.date_of_invoice = {
          comparator: ListSearchComparator.LessThanOrEqual,
          value: filters.dateTo
        };
      }
    }
    hasFilters = true;
  }

  if (filters.accountCode) {
    const accountCode = accountCodes.find(
      (code) => code.id === filters.accountCode
    );
    if (accountCode?.code) {
      criteria.extracted_category_code = {
        comparator: ListSearchComparator.Equal,
        value: accountCode.code
      };
      hasFilters = true;
    }
    if (accountCode?.name) {
      criteria.extracted_category_name = {
        comparator: ListSearchComparator.Equal,
        value: accountCode.name
      };
      hasFilters = true;
    }
  }

  if (filters.vatAmount) {
    criteria.vat = {
      comparator: ListSearchComparator.Contains,
      value: formatNumericValue(filters.vatAmount)
    };
    hasFilters = true;
  }

  if (filters.uploadedDateFrom) {
    criteria.created_at = {
      comparator: ListSearchComparator.GreaterThanOrEqual,
      value: filters.uploadedDateFrom
    };
    hasFilters = true;
  }

  if (filters.uploadedDateTo) {
    // Set the time to end of day (23:59:59.999999) to include the entire day
    const endOfDay = new Date(filters.uploadedDateTo);
    endOfDay.setHours(23, 59, 59, 999);
    const endOfDayString = endOfDay.toISOString();

    if (criteria.created_at) {
      const dateFrom = criteria.created_at as ListSearchCriterion;
      criteria.created_at = [
        dateFrom,
        {
          comparator: ListSearchComparator.LessThanOrEqual,
          value: endOfDayString
        }
      ];
    } else {
      criteria.created_at = {
        comparator: ListSearchComparator.LessThanOrEqual,
        value: endOfDayString
      };
    }
    hasFilters = true;
  }

  if (filters.source) {
    criteria.source = {
      comparator: ListSearchComparator.Equal,
      value: filters.source
    };
    hasFilters = true;
  }

  if (filters.uploadedBy) {
    criteria.user_id = {
      comparator: ListSearchComparator.Equal,
      value: filters.uploadedBy
    };
    hasFilters = true;
  }

  if (filters.invoiceId) {
    criteria.invoice_id = {
      comparator: ListSearchComparator.Contains,
      value: filters.invoiceId
    };
    hasFilters = true;
  }

  return hasFilters ? criteria : undefined;
};

/**
 * Extract current filter values from search criteria
 */
const extractFiltersFromCriteria = (
  searchCriteria?: ListSearchCriteria
): FilterFields => {
  if (!searchCriteria) {
    return {};
  }

  const filters: FilterFields = {};

  if (searchCriteria.supplier) {
    const criteria = Array.isArray(searchCriteria.supplier)
      ? searchCriteria.supplier[0]
      : searchCriteria.supplier;

    if (criteria?.comparator === ListSearchComparator.Contains) {
      filters.supplier = String(criteria.value);
    }
  }
  if (searchCriteria.total) {
    const criteria = Array.isArray(searchCriteria.total)
      ? searchCriteria.total[0]
      : searchCriteria.total;

    filters.total = String(criteria.value);
  }
  if (searchCriteria.date_of_invoice) {
    const dateValue = searchCriteria.date_of_invoice;

    if (Array.isArray(dateValue)) {
      // If it's an array, find the from and to date criteria
      dateValue.forEach((criteria) => {
        if (criteria?.comparator === ListSearchComparator.GreaterThanOrEqual) {
          filters.dateFrom = String(criteria.value);
        } else if (
          criteria?.comparator === ListSearchComparator.LessThanOrEqual
        ) {
          filters.dateTo = String(criteria.value);
        }
      });
    } else {
      // Handle single date criterion
      const singleDateCriterion = dateValue;
      if (
        singleDateCriterion.comparator ===
        ListSearchComparator.GreaterThanOrEqual
      ) {
        filters.dateFrom = String(singleDateCriterion.value);
      } else if (
        singleDateCriterion.comparator === ListSearchComparator.LessThanOrEqual
      ) {
        filters.dateTo = String(singleDateCriterion.value);
      }
    }
  }
  if (searchCriteria.account_code) {
    const criteria = Array.isArray(searchCriteria.account_code)
      ? searchCriteria.account_code[0]
      : searchCriteria.account_code;

    if (criteria?.comparator === ListSearchComparator.Contains) {
      filters.accountCode = String(criteria.value);
    }
  }
  if (searchCriteria.vat) {
    const criteria = Array.isArray(searchCriteria.vat)
      ? searchCriteria.vat[0]
      : searchCriteria.vat;

    filters.vatAmount = String(criteria.value);
  }
  if (searchCriteria.created_at) {
    const dateValue = searchCriteria.created_at;

    if (Array.isArray(dateValue)) {
      dateValue.forEach((criteria) => {
        if (criteria?.comparator === ListSearchComparator.GreaterThanOrEqual) {
          filters.uploadedDateFrom = String(criteria.value);
        } else if (
          criteria?.comparator === ListSearchComparator.LessThanOrEqual
        ) {
          filters.uploadedDateTo = String(criteria.value);
        }
      });
    } else {
      const singleDateCriterion = dateValue;
      if (
        singleDateCriterion.comparator ===
        ListSearchComparator.GreaterThanOrEqual
      ) {
        filters.uploadedDateFrom = String(singleDateCriterion.value);
      } else if (
        singleDateCriterion.comparator === ListSearchComparator.LessThanOrEqual
      ) {
        filters.uploadedDateTo = String(singleDateCriterion.value);
      }
    }
  }
  if (searchCriteria.source) {
    const criteria = Array.isArray(searchCriteria.source)
      ? searchCriteria.source[0]
      : searchCriteria.source;

    if (criteria?.comparator === ListSearchComparator.Contains) {
      filters.source = String(criteria.value);
    }
  }

  return filters;
};

const useCrudListFilter = (): UseCrudListFilterReturn => {
  const {
    searchCriteria,
    searchList,
    statusesFilter,
    filterListByStatus,
    dataType
  } = useCrudListContext();
  const accountingData = useSelector(selectAccountingShortData);
  const accountCodes = useMemo<AccountWithCode[]>(
    () => accountingData?.accounts ?? [],
    [accountingData?.accounts]
  );

  // Extract current filter values from criteria
  const currentFilters = useMemo<FilterFields>(() => {
    const filters = extractFiltersFromCriteria(searchCriteria);
    if (statusesFilter) {
      filters.status = statusesFilter;
    }
    return filters;
  }, [searchCriteria, statusesFilter]);

  // Apply new filters
  const applyFilters = useCallback(
    (filters: FilterFields) => {
      if (filters.status) {
        filterListByStatus(filters.status);
      }
      const newSearchCriteria = createFilterCriteria(
        filters,
        accountCodes,
        dataType
      );
      searchList(newSearchCriteria);
    },
    [searchList, filterListByStatus, accountCodes, dataType]
  );

  // Clear all filters
  const clearFilters = useCallback(() => {
    filterListByStatus([]);
    searchList(undefined);
  }, [searchList, filterListByStatus]);

  return {
    applyFilters,
    clearFilters,
    currentFilters
  };
};

export default useCrudListFilter;
